import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CartProvider } from "@/contexts/CartContext";
import { OrderTypeProvider } from "@/contexts/OrderTypeContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { AuthProvider } from "@/hooks/useAuth";
import { SessionProvider } from "@/contexts/SessionContext";
import { BranchModalProvider } from "@/contexts/BranchModalContext";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import BranchModal from "@/components/BranchModal/BranchModal";
import LoginModal from "@/components/LoginModal/LoginModal";
import Index from "./pages/Index";
import MenuPage from "./pages/MenuPage";
import CartPage from "./pages/CartPage";
import ProfilePage from "./pages/ProfilePage";
import AuthPage from "./pages/AuthPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import TrackPage from "./pages/TrackPage";
import TrackOrderLookup from "./pages/TrackOrderLookup";
import PaymentPage from "./pages/PaymentPage";
import BranchesPage from "./pages/BranchesPage";
import AboutPage from "./pages/AboutPage";
import NotFound from "./pages/NotFound";
import ChatWidget from "@/components/shared/ChatWidget";
const queryClient = new QueryClient();

const App = () => {
  const params = new URLSearchParams(window.location.search);
  const restaurantId = params.get("r") || undefined;

  // Hide splash loader when React mounts
  useEffect(() => {
    const splash = document.getElementById("splash-loader");
    if (splash) {
      splash.classList.add("hidden");
      setTimeout(() => splash.remove(), 350);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SessionProvider>
          <BrandProvider restaurantId={restaurantId}>
            <LanguageProvider>
              <ThemeProvider>
                <CartProvider>
                  <OrderTypeProvider>
                    <BranchModalProvider>
                      <CustomerAuthProvider>
                        <TooltipProvider>
                          <Toaster />
                          <Sonner />
                          <HashRouter>
                            <BranchModal />
                            <LoginModal />
                            <Routes>
                              <Route path="/" element={<Index />} />
                              <Route path="/menu" element={<MenuPage />} />
                              <Route path="/cart" element={<CartPage />} />
                              <Route
                                path="/profile"
                                element={<ProfilePage />}
                              />
                              <Route path="/auth" element={<AuthPage />} />
                              <Route
                                path="/payment"
                                element={<PaymentPage />}
                              />
                              <Route
                                path="/branches"
                                element={<BranchesPage />}
                              />
                              <Route path="/about" element={<AboutPage />} />
                              <Route
                                path="/track"
                                element={<TrackOrderLookup />}
                              />
                              <Route
                                path="/track/:orderId"
                                element={<TrackPage />}
                              />
                              <Route
                                path="/admin"
                                element={
                                  <ProtectedRoute>
                                    <AdminDashboard />
                                  </ProtectedRoute>
                                }
                              />
                              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                              <Route path="*" element={<NotFound />} />
                              {/* <Route path="*" element={<NotFound />} /> */}
                            </Routes>
                            {/* <ChatWidget /> */}
                          </HashRouter>
                        </TooltipProvider>
                      </CustomerAuthProvider>
                    </BranchModalProvider>
                  </OrderTypeProvider>
                </CartProvider>
              </ThemeProvider>
            </LanguageProvider>
          </BrandProvider>
        </SessionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
